import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as fbs from "@/api/fbs";
import * as pickersApi from "@/api/one_c_pickers";
import { PickerField } from "@/components/pickers/PickerField";
import { RemotePicker, savedPickerValue } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError } from "@/lib/api-errors";
import { searchOrganizations, searchWarehouses } from "@/pages/settings/cards/one-c-search";
import { usePortalConfig } from "@/portal/usePortalConfig";

const EMPTY_CABINET: fbs.CabinetPayload = {
  name: "",
  infisical_slug: "",
  wb_sandbox: false,
  wb_warehouse_id: 0,
  organization_ref: "",
  organization_name: "",
  kontragent_ref: "",
  kontragent_name: "",
  contract_ref: "",
  contract_name: "",
  sale_kontragent_ref: "",
  sale_kontragent_name: "",
  sale_contract_ref: "",
  sale_contract_name: "",
  bank_account_ref: "",
  bank_account_name: "",
  warehouses: [],
};

/** Кабинет из списка → форма. Пары ref+name переносим как есть: незатронутое
 *  поле должно сохраниться ровно тем, чем пришло с сервера. */
function cabinetToPayload(cabinet: fbs.Cabinet): fbs.CabinetPayload {
  return {
    name: cabinet.name,
    infisical_slug: cabinet.infisical_slug,
    wb_sandbox: cabinet.wb_sandbox,
    wb_warehouse_id: cabinet.wb_warehouse_id,
    organization_ref: cabinet.organization_ref,
    organization_name: cabinet.organization_name,
    kontragent_ref: cabinet.kontragent_ref ?? "",
    kontragent_name: cabinet.kontragent_name ?? "",
    contract_ref: cabinet.contract_ref ?? "",
    contract_name: cabinet.contract_name ?? "",
    sale_kontragent_ref: cabinet.sale_kontragent_ref ?? "",
    sale_kontragent_name: cabinet.sale_kontragent_name ?? "",
    sale_contract_ref: cabinet.sale_contract_ref ?? "",
    sale_contract_name: cabinet.sale_contract_name ?? "",
    bank_account_ref: cabinet.bank_account_ref ?? "",
    bank_account_name: cabinet.bank_account_name ?? "",
    warehouses: cabinet.warehouses,
  };
}

/** Редактор списка складов продавца.
 *
 * Список, а не одно поле: склады кабинета работают ОДНОВРЕМЕННО, у каждого своя
 * поставка в WB и может быть свой склад в 1С (тогда и добор на них разный). */
function WarehousesField({
  value,
  onChange,
}: {
  value: fbs.CabinetWarehouse[];
  onChange: (v: fbs.CabinetWarehouse[]) => void;
}): React.ReactElement {
  const patch = (i: number, next: Partial<fbs.CabinetWarehouse>) =>
    onChange(value.map((w, idx) => (idx === i ? { ...w, ...next } : w)));

  return (
    <div className="sm:col-span-2">
      <Label className="text-xs">Склады продавца в WB</Label>
      <div className="mt-1 space-y-2">
        {value.map((wh, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[7rem,1fr,1fr,auto]">
            <Input
              type="number"
              value={wh.wb_warehouse_id || ""}
              placeholder="id склада"
              onChange={(e) => patch(i, { wb_warehouse_id: Number(e.target.value || 0) })}
            />
            <Input
              value={wh.name}
              placeholder="Володарского"
              onChange={(e) => patch(i, { name: e.target.value })}
            />
            {/* ⚠️ ВЫБОР из 1С, а не ввод. Это поле было единственным свободным
                среди пикеров, и у второй инсталляции сюда вписали ссылки ИЗ
                ИНТЕРФЕЙСА 1С (32 hex без дефисов, другой порядок байтов).
                Портал их принял, а 1С на запрос ответила «Ошибка при разборе
                опции запроса $filter» — остаток комнаты не показывался вовсе,
                и по сообщению причину было не прочесть. */}
            <RemotePicker
              value={savedPickerValue(wh.one_c_warehouse_ref, wh.one_c_warehouse_name)}
              onChange={(row) =>
                patch(i, {
                  one_c_warehouse_ref: row?.ref ?? null,
                  one_c_warehouse_name: row?.name ?? null,
                })
              }
              search={searchWarehouses}
              queryKey={["one-c", "warehouses"]}
              placeholder="склад 1С — если их несколько"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </div>
        ))}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onChange([...value, { wb_warehouse_id: 0, name: "" }])}
        >
          <Plus className="mr-1 size-4" /> Склад
        </Button>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        склады работают одновременно: у каждого своя поставка в WB. Склад 1С
        заполняется, только когда складов несколько — добор тогда ведётся на
        каждый отдельно
      </p>
    </div>
  );
}

/**
 * ⚠️ Через `toastError`, а не голым `detail`: у FastAPI 422 `detail` — МАССИВ
 * объектов, и `toast.error` показал бы «[object Object]» (а sonner на массиве
 * объектов и вовсе падает). 422 здесь достижим: ссылки 1С проверяются при
 * сохранении, а в базе второй инсталляции уже лежат кривые — открыть форму и
 * нажать «Сохранить» достаточно. Отказ, который не называет причину, ничем не
 * лучше молчания, которое он заменил.
 */
const apiError = toastError();

export function FbsCabinetsCard(): React.ReactElement {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["fbs-cabinets"], queryFn: fbs.listCabinets });
  // Где инсталляция держит токены WB — свойство профиля, а не кабинета.
  const { data: config } = useQuery({ queryKey: ["fbs-config"], queryFn: fbs.getConfig });
  const tokenInPortal = config?.wb_token_in_portal ?? false;
  const [draft, setDraft] = useState<fbs.CabinetPayload | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["fbs-cabinets"] });

  const createMut = useMutation({
    mutationFn: fbs.createCabinet,
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success("Кабинет добавлен");
    },
    onError: apiError,
  });
  const deleteMut = useMutation({
    mutationFn: fbs.deleteCabinet,
    onSuccess: () => {
      invalidate();
      toast.success("Кабинет удалён");
    },
    onError: apiError,
  });
  const testMut = useMutation({
    mutationFn: fbs.testCabinet,
    onSuccess: (r) =>
      r.ok
        ? toast.success(
            `WB отвечает: заданий ${r.orders_total ?? 0}, на нашем складе ${r.orders_on_warehouse ?? 0}`,
          )
        : toast.error(r.error ?? "Кабинет недоступен"),
    onError: apiError,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Кабинеты WB</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setDraft({ ...EMPTY_CABINET })}>
          <Plus className="mr-1 size-4" /> Добавить
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <Loader2 className="size-4 animate-spin" />}
        {!isLoading && (data ?? []).length === 0 && !draft && (
          <p className="text-sm text-muted-foreground">
            Кабинетов нет — сбор ФБС начать не получится. Добавьте кабинет с токеном WB API
            и id склада продавца.
          </p>
        )}

        {(data ?? []).map((cabinet) => (
          <CabinetRow
            key={cabinet.id}
            cabinet={cabinet}
            onTest={() => testMut.mutate(cabinet.id)}
            onDelete={() => deleteMut.mutate(cabinet.id)}
            busy={testMut.isPending || deleteMut.isPending}
            tokenInPortal={tokenInPortal}
          />
        ))}

        {draft && (
          <CabinetForm
            value={draft}
            onChange={setDraft}
            onCancel={() => setDraft(null)}
            onSave={() => createMut.mutate(draft)}
            saving={createMut.isPending}
            tokenInPortal={tokenInPortal}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** Строка реквизита в свёрнутой карточке: показываем имя, а если его нет —
 *  ссылку. Пустое «—» означает, что реквизит действительно не заполнен. */
function RefLine({ label, refValue, name }: {
  label: string;
  refValue: string | null;
  name: string | null;
}): React.ReactElement {
  const shown = (name ?? "").trim() || (refValue ?? "").trim();
  return (
    <div className="flex gap-1">
      <span className="text-muted-foreground">{label}:</span>
      <span className={shown ? "truncate" : "text-muted-foreground"}>{shown || "—"}</span>
    </div>
  );
}

function CabinetRow({
  cabinet,
  onTest,
  onDelete,
  busy,
  tokenInPortal,
}: {
  cabinet: fbs.Cabinet;
  onTest: () => void;
  onDelete: () => void;
  busy: boolean;
  tokenInPortal: boolean;
}): React.ReactElement {
  const { ptmo } = usePortalConfig();
  const qc = useQueryClient();
  // null — карточка свёрнута. Форма правки одна на все реквизиты: раньше
  // реквизиты продажи жили отдельным блоком и дублировали те же поля.
  const [form, setForm] = useState<fbs.CabinetPayload | null>(null);

  const updateMut = useMutation({
    mutationFn: (payload: fbs.CabinetPayload) => fbs.updateCabinet(cabinet.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fbs-cabinets"] });
      setForm(null);
      toast.success("Сохранено");
    },
    onError: apiError,
  });

  return (
    <div className="rounded-lg border p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium">{cabinet.name}</div>
          <div className="text-xs text-muted-foreground">
            Склад продавца WB: {cabinet.wb_warehouse_id} · организация{" "}
            {cabinet.organization_name || cabinet.organization_ref}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="sm" variant="outline" onClick={onTest} disabled={busy}>
            Проверить
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setForm(form ? null : cabinetToPayload(cabinet))}
            disabled={busy}
          >
            <Pencil className="mr-1 size-4" /> {form ? "Свернуть" : "Изменить"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} disabled={busy}>
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      </div>

      {cabinet.wb_sandbox && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
          Тестовый контур WB (песочница): запросы уходят на
          marketplace-api-sandbox.wildberries.ru. Боевой токен там отвечает
          403 «production token used for sandbox endpoint». Снимите галку в
          форме кабинета, если кабинет боевой.
        </p>
      )}
      {!cabinet.ready_for_documents && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
          {cabinet.ready_error}
        </p>
      )}
      {!cabinet.ready_for_sale && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
          {cabinet.sale_error}
        </p>
      )}
      {!cabinet.token_ready && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
          {cabinet.token_error}
        </p>
      )}

      {form ? (
        <div className="mt-3">
          <CabinetForm
            value={form}
            onChange={setForm}
            onCancel={() => setForm(null)}
            onSave={() => updateMut.mutate(form)}
            saving={updateMut.isPending}
            tokenInPortal={tokenInPortal}
          />
        </div>
      ) : (
        <div className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
          {ptmo && (
            <RefLine
              label="Получатель передачи"
              refValue={cabinet.kontragent_ref}
              name={cabinet.kontragent_name}
            />
          )}
          {ptmo && (
            <RefLine
              label="Договор передачи"
              refValue={cabinet.contract_ref}
              name={cabinet.contract_name}
            />
          )}
          <RefLine
            label="Покупатель"
            refValue={cabinet.sale_kontragent_ref}
            name={cabinet.sale_kontragent_name}
          />
          <RefLine
            label="Договор продажи"
            refValue={cabinet.sale_contract_ref}
            name={cabinet.sale_contract_name}
          />
          <RefLine
            label="Банковский счёт"
            refValue={cabinet.bank_account_ref}
            name={cabinet.bank_account_name}
          />
          <RefLine
            label="Токен WB"
            refValue={cabinet.token_ready ? "настроен" : null}
            name={cabinet.token_ready ? "настроен" : cabinet.token_error}
          />
        </div>
      )}
    </div>
  );
}

function CabinetForm({
  value,
  onChange,
  onCancel,
  onSave,
  saving,
  tokenInPortal,
}: {
  value: fbs.CabinetPayload;
  onChange: (v: fbs.CabinetPayload) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  tokenInPortal: boolean;
}): React.ReactElement {
  const { ptmo } = usePortalConfig();
  const field = (
    key: "name" | "infisical_slug" | "wb_warehouse_id" | "wb_token",
    label: string,
    hint?: string,
    type: "text" | "number" = "text",
  ) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={(value[key] as string | number | undefined) ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            [key]: type === "number" ? Number(e.target.value || 0) : e.target.value,
          })
        }
      />
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );

  const orgRef = (value.organization_ref ?? "").trim();
  const kontragentRef = (value.kontragent_ref ?? "").trim();
  const saleKontragentRef = (value.sale_kontragent_ref ?? "").trim();

  return (
    <div className="space-y-3 rounded-lg border border-dashed p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {field("name", "Название", "как показывать кладовщику: «Омегавита»")}
        {field("wb_warehouse_id", "ID склада продавца в WB", "поставка = кабинет + склад", "number")}
        {/* ⚠️ Галки во фронте не было вовсе, хотя флаг в базе есть и он
            переключает базовый адрес WB. Единственным её проявлением был 403
            «production token used for sandbox endpoint», по которому причину
            не прочесть. */}
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(value.wb_sandbox)}
            onChange={(e) => onChange({ ...value, wb_sandbox: e.target.checked })}
          />
          <span>
            Тестовый контур WB (песочница)
            <span className="block text-xs text-muted-foreground">
              Запросы уходят на marketplace-api-sandbox.wildberries.ru и токен
              берётся из песочной папки. У боевого кабинета галка снята.
            </span>
          </span>
        </label>
        <WarehousesField
          value={value.warehouses ?? []}
          onChange={(warehouses) => onChange({ ...value, warehouses })}
        />
        {tokenInPortal
          ? field(
              "wb_token",
              "Токен WB (Маркетплейс, с правом записи)",
              "хранится в базе портала зашифрованным; поле всегда пустое — введите новый, чтобы заменить",
            )
          : field(
              "infisical_slug",
              "Папка в Infisical",
              "omegavita / ip-egrashkin — токен WB портал берёт оттуда в рантайме и в своей базе не хранит",
            )}

        <PickerField
          label="Организация 1С"
          hint="юрлицо-продавец"
          placeholder="Начните вводить название организации…"
          value={savedPickerValue(value.organization_ref, value.organization_name)}
          search={searchOrganizations}
          queryKey={["one-c-organizations"]}
          // Организация — родитель договоров и счёта: у другого юрлица и договоры,
          // и счета свои. При смене обнуляем зависимые ссылки — иначе в кабинете
          // останется договор чужого юрлица и документы уйдут не туда.
          onChange={(r) =>
            onChange({
              ...value,
              organization_ref: r?.ref ?? "",
              organization_name: r?.name ?? "",
              contract_ref: "",
              contract_name: "",
              sale_contract_ref: "",
              sale_contract_name: "",
              bank_account_ref: "",
              bank_account_name: "",
            })
          }
        />

        {ptmo && (
        <PickerField
          label="Контрагент-получатель передачи"
          hint="для ПТМО"
          placeholder="Начните вводить название контрагента…"
          value={savedPickerValue(value.kontragent_ref, value.kontragent_name)}
          search={(q) => pickersApi.searchKontragents(q)}
          queryKey={["one-c-kontragents"]}
          // Договор принадлежит паре «организация × контрагент»: сменили
          // контрагента — прошлый договор больше не его.
          onChange={(r) =>
            onChange({
              ...value,
              kontragent_ref: r?.ref ?? "",
              kontragent_name: r?.name ?? "",
              contract_ref: "",
              contract_name: "",
            })
          }
        />
        )}

        {ptmo && (
        <PickerField
          label="Договор передачи"
          hint="из него берётся вид цен"
          blockedHint={
            !orgRef || !kontragentRef
              ? "Сначала выберите организацию и контрагента-получателя"
              : undefined
          }
          placeholder="Начните вводить название договора…"
          value={savedPickerValue(value.contract_ref, value.contract_name)}
          // Передача идёт между своими юрлицами — вид договора там не
          // «СКомиссионером», поэтому фильтр по виду снимаем пустой строкой.
          search={(q) =>
            pickersApi.searchDogovors(kontragentRef, q, {
              organizationRef: orgRef,
              vidDogovora: "",
            })
          }
          queryKey={["one-c-dogovors", "transfer", orgRef, kontragentRef]}
          onChange={(r) =>
            onChange({ ...value, contract_ref: r?.ref ?? "", contract_name: r?.name ?? "" })
          }
        />
        )}

        <PickerField
          label="Покупатель (маркетплейс)"
          hint="для заказа покупателя и расходной"
          placeholder="Начните вводить название контрагента…"
          value={savedPickerValue(value.sale_kontragent_ref, value.sale_kontragent_name)}
          search={(q) => pickersApi.searchKontragents(q)}
          queryKey={["one-c-kontragents"]}
          onChange={(r) =>
            onChange({
              ...value,
              sale_kontragent_ref: r?.ref ?? "",
              sale_kontragent_name: r?.name ?? "",
              // Договор продажи — договор ИМЕННО этого покупателя.
              sale_contract_ref: "",
              sale_contract_name: "",
            })
          }
        />

        <PickerField
          label="Договор продажи"
          hint="комиссионный; из него берётся вид цен"
          blockedHint={
            !orgRef || !saleKontragentRef
              ? "Сначала выберите организацию и покупателя"
              : undefined
          }
          placeholder="Начните вводить название договора…"
          value={savedPickerValue(value.sale_contract_ref, value.sale_contract_name)}
          search={(q) =>
            pickersApi.searchDogovors(saleKontragentRef, q, { organizationRef: orgRef })
          }
          queryKey={["one-c-dogovors", "sale", orgRef, saleKontragentRef]}
          onChange={(r) =>
            onChange({
              ...value,
              sale_contract_ref: r?.ref ?? "",
              sale_contract_name: r?.name ?? "",
            })
          }
        />

        <PickerField
          label="Банковский счёт организации"
          hint="свой у каждого юрлица"
          blockedHint={!orgRef ? "Сначала выберите организацию" : undefined}
          placeholder="Начните вводить номер или банк…"
          value={savedPickerValue(value.bank_account_ref, value.bank_account_name)}
          search={(q) => pickersApi.searchBankAccounts(orgRef, q)}
          queryKey={["one-c-bank-accounts", orgRef]}
          onChange={(r) =>
            onChange({
              ...value,
              bank_account_ref: r?.ref ?? "",
              bank_account_name: r?.name ?? "",
            })
          }
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSave} disabled={saving}>
          Сохранить
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </div>
  );
}
